import { WarningHex } from '@carbon/icons-react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { type VariantProps, cva } from 'class-variance-authority';
import { CheckCircle, X } from 'lucide-react';
import * as React from 'react';

import { cn } from 'DesignSystem/utils';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
  // eslint-disable-next-line react/prop-types
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed bottom-0 z-[100] flex max-h-screen w-full flex-col p-4 sm:bottom-8 sm:right-8 sm:w-[432px] sm:max-w-[calc(100vw-64px)] sm:p-0',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        destructive: 'destructive group border-destructive bg-destructive text-destructive-foreground',
        success:
          'success group w-[432px] max-w-[calc(100vw-32px)] items-center gap-6 space-x-0 rounded-[6px] border-0 border-l-4 border-l-[#059669] bg-white px-4 py-3 pr-3 text-zinc-800 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]',
        error:
          'error group w-[432px] max-w-[calc(100vw-32px)] items-start gap-0 space-x-0 rounded-[6px] border-0 border-l-4 border-l-[#DC2626] bg-white px-4 py-3 pr-3 text-zinc-800 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return <ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />;
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
  // eslint-disable-next-line react/prop-types
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-dark focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      'group-[.success]:h-6 group-[.success]:rounded-[5px] group-[.success]:border-0 group-[.success]:bg-zinc-900 group-[.success]:px-[5px] group-[.success]:text-xs group-[.success]:leading-5 group-[.success]:text-white group-[.success]:hover:bg-zinc-800',
      'group-[.error]:mt-3 group-[.error]:h-6 group-[.error]:w-fit group-[.error]:rounded-[5px] group-[.error]:border group-[.error]:border-zinc-200 group-[.error]:bg-white group-[.error]:px-2 group-[.error]:text-xs group-[.error]:font-medium group-[.error]:leading-5 group-[.error]:text-zinc-900 group-[.error]:hover:bg-zinc-50',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
  // eslint-disable-next-line react/prop-types
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
      'group-[.error]:hidden group-[.success]:hidden',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
  // eslint-disable-next-line react/prop-types
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      'text-sm font-semibold group-[.success]:text-[15px] group-[.success]:font-medium group-[.success]:leading-[19px] group-[.success]:text-zinc-800',
      'group-[.error]:text-[15px] group-[.error]:font-medium group-[.error]:leading-[19px] group-[.error]:text-zinc-800',
      className
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
  // eslint-disable-next-line react/prop-types
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      'text-sm opacity-90 group-[.success]:text-xs group-[.success]:leading-[15px] group-[.success]:text-zinc-800 group-[.success]:opacity-100',
      'group-[.error]:text-xs group-[.error]:leading-[15px] group-[.error]:text-zinc-800 group-[.error]:opacity-100',
      className
    )}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

const ToastIcon = ({ variant }: { variant?: ToastProps['variant'] }) => {
  if (variant === 'success') {
    return <CheckCircle className="h-6 w-6 shrink-0 text-amber-950" strokeWidth={1.75} />;
  }

  if (variant === 'error') {
    return <WarningHex size={24} className="shrink-0 text-[#DC2626]" aria-hidden />;
  }

  return null;
};

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastIcon,
};
